import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { ProductQuoteResult } from '@typesApp/ProductQuote'
import { CommerceProductQuoteRequestItem } from '@typesApp/Service'
import { useClaimRecord } from '@hooks/useClaimRecord'
import { useLazyGetClaimRecordNoCacheQuery, useLazyGetProductQuoteQuery } from '@redux/queries/claimRecords'
import {
  setProductQuoteLoading,
  setProductQuoteError,
  setProductQuoteSuccess,
} from '@redux/slices/insuranceMemberSlice'
import { getServiceStatus } from '@app/events/helpers/getServiceStatus'
import { mapCommerceProductQuoteRequest } from '@app/events/helpers/mapCommerceProductQuoteRequest'

const EMPTY_RESULT: ProductQuoteResult = {
  productQuotes: [],
}

type UseProductQuoteReturn = {
  isLoading: boolean
  isError: boolean
  requestQuote: (request: CommerceProductQuoteRequestItem[]) => Promise<ProductQuoteResult>
}

export const useProductQuoteService = (): UseProductQuoteReturn => {
  const dispatch = useDispatch()
  const { claimRecordId } = useClaimRecord()
  const [fetchClaimRecordNoCache] = useLazyGetClaimRecordNoCacheQuery()
  const [triggerRequest, { isLoading, isError }] = useLazyGetProductQuoteQuery()

  const requestQuote = useCallback(
    async (request: CommerceProductQuoteRequestItem[]): Promise<ProductQuoteResult> => {
      dispatch(setProductQuoteLoading())

      if (!claimRecordId) {
        return EMPTY_RESULT
      }

      try {
        // always fetch latest claim record
        const claimRecord = await fetchClaimRecordNoCache(claimRecordId).unwrap()
        if (!claimRecord) {
          return EMPTY_RESULT
        }

        const status = getServiceStatus('product-quote', { claimRecord, claimRecordId, claimRecordDeleted: false })
        if (status !== 'ready') {
          return EMPTY_RESULT
        }

        const products = mapCommerceProductQuoteRequest(request)
        const result = await triggerRequest({ claimRecordId, products }).unwrap()
        dispatch(setProductQuoteSuccess(result))
        return result
      } catch (error) {
        dispatch(setProductQuoteError(true))
        return { ...EMPTY_RESULT, isError: true }
      }
    },
    [dispatch, triggerRequest, fetchClaimRecordNoCache, claimRecordId]
  )

  return {
    isLoading,
    isError,
    requestQuote,
  }
}
